import { Fragment, useState } from "react";
import { IconBook } from "@tabler/icons-react";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import EmptyState from "../UI/EmptyState";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { dateConvert } from "../../api/Functions";
import MoisPicker from "./MoisPicker";
import { useJournalComptable } from "../../hooks/use-compta-pro";
import type { JournalCode } from "../../models/comptabilite";
import {
  euros,
  JOURNAL_LABELS,
  periodeCourante,
  type Periode,
} from "./comptaUtils";
import styles from "./Compta.module.css";

interface JournalOption {
  value: JournalCode | "";
  label: string;
}

const JOURNAL_OPTIONS: JournalOption[] = [
  { value: "", label: "Tous les journaux" },
  ...(Object.entries(JOURNAL_LABELS) as [JournalCode, string][]).map(
    ([value, label]) => ({ value, label: `${value} — ${label}` }),
  ),
];

export default function JournalTab() {
  const [periode, setPeriode] = useState<Periode>(periodeCourante());
  const [journalFilter, setJournalFilter] = useState<JournalOption>(
    JOURNAL_OPTIONS[0],
  );
  const { data, isLoading } = useJournalComptable(periode.year, periode.month);

  const entries = (data?.entries ?? []).filter(
    (entry) => !journalFilter.value || entry.journal === journalFilter.value,
  );

  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <MoisPicker
          label="Mois"
          className={styles.moisPicker}
          value={periode}
          onChange={setPeriode}
        />
        <ReactSelectStandalone
          label="Journal"
          className={styles.journalSelect}
          options={JOURNAL_OPTIONS}
          value={journalFilter}
          onChange={(option: JournalOption) => setJournalFilter(option)}
        />
        <div className={styles.toolbarSpacer}>
          {data && (
            <Status
              color={data.status === "closed" ? "success" : "info"}
              text={
                data.status === "closed"
                  ? "Période clôturée"
                  : "Période ouverte"
              }
            />
          )}
        </div>
      </div>
      {data && data.anomalies.length > 0 && (
        <Callout variant="warning" title="Anomalies de projection">
          {data.anomalies.join(" — ")}
        </Callout>
      )}
      {isLoading ? (
        <IsLoading />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={IconBook}
          title="Aucune écriture sur la période"
          description="Les écritures apparaissent dès qu'un mandat de gestion est actif et que des opérations existent sur le mois."
        />
      ) : (
        <Table variant="card">
          <Table.Header>
            <Table.Column>Date</Table.Column>
            <Table.Column>Journal</Table.Column>
            <Table.Column>N°</Table.Column>
            <Table.Column>Pièce</Table.Column>
            <Table.Column>Libellé</Table.Column>
            <Table.Column>Compte</Table.Column>
            <Table.Column align="right">Débit</Table.Column>
            <Table.Column align="right">Crédit</Table.Column>
          </Table.Header>
          <Table.Body>
            {entries.map((entry) => (
              <Fragment key={entry.piece_ref}>
                {entry.lines.map((line, index) => (
                  <Table.Row key={`${entry.piece_ref}-${line.numero}-${index}`}>
                    {index === 0 && (
                      <>
                        <Table.Cell rowSpan={entry.lines.length}>
                          {dateConvert(entry.ecriture_date)}
                        </Table.Cell>
                        <Table.Cell rowSpan={entry.lines.length}>
                          <Status color="neutral" text={entry.journal} />
                        </Table.Cell>
                        <Table.Cell rowSpan={entry.lines.length}>
                          {entry.num ?? "—"}
                        </Table.Cell>
                        <Table.Cell rowSpan={entry.lines.length}>
                          {entry.piece_ref}
                        </Table.Cell>
                        <Table.Cell rowSpan={entry.lines.length}>
                          {entry.label}
                        </Table.Cell>
                      </>
                    )}
                    <Table.Cell>{line.numero}</Table.Cell>
                    <Table.Cell align="right">
                      {parseFloat(line.debit) !== 0 ? euros(line.debit) : ""}
                    </Table.Cell>
                    <Table.Cell align="right">
                      {parseFloat(line.credit) !== 0 ? euros(line.credit) : ""}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Fragment>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}
